'use client'

import { useCallback, useState } from 'react'

import { useTranslations } from 'next-intl'

import { cn } from '@/lib/utils/cn'

import { GLYPH_PATH, SOLID_BG, TONES, WORDMARK_PATH } from './company-data'

type Surface = 'solid' | 'transparent'

export function BrandPageContent() {
  const t = useTranslations('company')
  const [surface, setSurface] = useState<Surface>('solid')
  const [square, setSquare] = useState(true)

  const handleDownloadSvg = useCallback(
    async (kind: 'h' | 'glyph', toneId: string) => {
      const { downloadSvg, buildSvg } = await import('./brand-download')
      const tone = TONES.find(tn => tn.id === toneId)
      if (!tone) return
      const svg = buildSvg(kind, tone, { square, solid: surface === 'solid' })
      downloadSvg(`digen-${kind}-${tone.id}.svg`, svg)
    },
    [square, surface]
  )

  const handleDownloadPng = useCallback(
    async (kind: 'h' | 'glyph', toneId: string) => {
      const { savePng } = await import('./brand-download')
      const tone = TONES.find(tn => tn.id === toneId)
      if (!tone) return
      try {
        await savePng(kind, tone, square, surface === 'solid')
      } catch {
        // ignore
      }
    },
    [square, surface]
  )

  const handleDownloadAll = useCallback(async () => {
    const { downloadAllZip } = await import('./brand-download')
    try {
      await downloadAllZip(square, surface === 'solid')
    } catch {
      // ignore
    }
  }, [square, surface])

  return (
    <section className="page">
      <div className="shell">
        <h1>{t('brand.title')}</h1>
        <p className="lead">{t('brand.lead')}</p>
        <div className="brand-bar">
          <div className="seg" role="tablist">
            <button
              className={surface === 'solid' ? 'is-on' : undefined}
              type="button"
              onClick={() => setSurface('solid')}
            >
              {t('brand.solidBg')}
            </button>
            <button
              className={surface === 'transparent' ? 'is-on' : undefined}
              type="button"
              onClick={() => setSurface('transparent')}
            >
              {t('brand.transparentBg')}
            </button>
          </div>
          <button className="solid" type="button" onClick={handleDownloadAll}>
            <DownloadIcon />
            {t('brand.downloadAll')}
          </button>
        </div>

        <div className="asset-block">
          <header>
            <div>
              <h2>{t('brand.horizontal')}</h2>
              <p className="hint">{t('brand.horizontalHint')}</p>
            </div>
          </header>
          <div className="grid-4">
            {TONES.map(tone => (
              <AssetCard
                key={tone.id}
                kind="h"
                pngLabel={t('brand.downloadPng')}
                surface={surface}
                svgLabel={t('brand.downloadSvg')}
                tone={tone}
                onPng={() => handleDownloadPng('h', tone.id)}
                onSvg={() => handleDownloadSvg('h', tone.id)}
              />
            ))}
          </div>
        </div>

        <div className="asset-block">
          <header>
            <div>
              <h2>{t('brand.glyph')}</h2>
              <p className="hint">{t('brand.glyphHint')}</p>
            </div>
            <label className="check">
              <input
                checked={square}
                type="checkbox"
                onChange={e => setSquare(e.target.checked)}
              />
              {t('brand.squareCanvas')}
            </label>
          </header>
          <div className="grid-4">
            {TONES.map(tone => (
              <AssetCard
                key={tone.id}
                kind="glyph"
                pngLabel={t('brand.downloadPng')}
                square={square}
                surface={surface}
                svgLabel={t('brand.downloadSvg')}
                tone={tone}
                onPng={() => handleDownloadPng('glyph', tone.id)}
                onSvg={() => handleDownloadSvg('glyph', tone.id)}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

function checkerboard(color: string) {
  return {
    backgroundImage: `linear-gradient(45deg, ${color} 25%, transparent 25%), linear-gradient(-45deg, ${color} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${color} 75%), linear-gradient(-45deg, transparent 75%, ${color} 75%)`,
    backgroundSize: '16px 16px',
    backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0'
  }
}

function getAssetStageStyle(surface: Surface, tone: (typeof TONES)[number]) {
  if (surface === 'solid') {
    return { backgroundColor: SOLID_BG[tone.id] }
  }

  if (tone.id === 'ink') {
    return {
      backgroundColor: '#efebe3',
      ...checkerboard('rgba(18, 18, 20, 0.12)')
    }
  }

  return checkerboard('rgba(243, 241, 234, 0.07)')
}

function readToneCopy(
  t: ReturnType<typeof useTranslations<'company.brand'>>,
  id: (typeof TONES)[number]['id']
) {
  switch (id) {
    case 'gold':
      return { name: t('goldName'), use: t('goldUse') }
    case 'flare':
      return { name: t('flareName'), use: t('flareUse') }
    case 'ink':
      return { name: t('inkName'), use: t('inkUse') }
    case 'cloud':
      return { name: t('cloudName'), use: t('cloudUse') }
  }
}

function AssetCard({
  kind,
  tone,
  surface,
  square,
  onSvg,
  onPng,
  svgLabel,
  pngLabel
}: {
  kind: 'h' | 'glyph'
  tone: (typeof TONES)[number]
  surface: Surface
  square?: boolean
  onSvg: () => void
  onPng: () => void
  svgLabel: string
  pngLabel: string
}) {
  const t = useTranslations('company.brand')
  const copy = readToneCopy(t, tone.id)
  const bgStyle = getAssetStageStyle(surface, tone)

  return (
    <article className={cn('asset', kind === 'glyph' && square && 'is-square')}>
      <div
        className={cn('asset-stage', kind === 'glyph' && 'is-glyph')}
        style={bgStyle}
      >
        {kind === 'glyph' ? (
          <svg fill="none" viewBox="0 0 19 20">
            <path d={GLYPH_PATH} fill={tone.hex} />
          </svg>
        ) : (
          <svg fill="none" viewBox="0 0 76 20">
            <path d={WORDMARK_PATH} fill={tone.hex} />
          </svg>
        )}
      </div>
      <div className="asset-meta">
        <strong>{copy.name}</strong>
        <small>{copy.use}</small>
        <div className="dl">
          <button type="button" onClick={onSvg}>
            <DownloadIcon />
            {svgLabel}
          </button>
          <button type="button" onClick={onPng}>
            <DownloadIcon />
            {pngLabel}
          </button>
        </div>
      </div>
    </article>
  )
}

function DownloadIcon() {
  return (
    <svg
      aria-hidden="true"
      fill="none"
      height="14"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      viewBox="0 0 24 24"
      width="14"
    >
      <path d="M12 3v12" />
      <path d="m8 11 4 4 4-4" />
      <path d="M5 21h14" />
    </svg>
  )
}
